import { DingTalkIcon } from '@/components/icons'
import { helpTopicUrl } from '@/lib/help-links'
import type { BlockConfig, BlockMeta } from '@/blocks/types'
import { AuthMode, IntegrationType } from '@/blocks/types'

export const DingTalkBlock: BlockConfig = {
  type: 'dingtalk',
  name: 'DingTalk',
  description: 'Read DingTalk tables and document blocks, and send robot messages',
  longDescription:
    'Connect a DingTalk internal app to read online table ranges, query document suite blocks, and send robot group messages. Document and table reads require an authorized operator unionId.',
  authMode: AuthMode.OAuth,
  category: 'tools',
  integrationType: IntegrationType.Productivity,
  bgColor: 'var(--integration-dingtalk)',
  icon: DingTalkIcon,
  docsLink: helpTopicUrl('integrations'),
  subBlocks: [
    {
      id: 'operation',
      value: () => 'read_table',
      title: 'Operation',
      type: 'dropdown',
      options: [
        {
          id: 'read_table',
          label: 'Read DingTalk table',
        },
        {
          id: 'read_document',
          label: 'Read DingTalk document blocks',
        },
        {
          id: 'send_message',
          label: 'Send DingTalk message',
        },
      ],
    },
    {
      id: 'credential',
      title: 'DingTalk account',
      type: 'oauth-input',
      credentialKind: 'service-account',
      serviceId: 'dingtalk',
      canonicalParamId: 'oauthCredential',
      mode: 'basic',
      required: true,
    },
    {
      id: 'manualCredential',
      title: 'DingTalk account',
      type: 'short-input',
      canonicalParamId: 'oauthCredential',
      mode: 'advanced',
      placeholder: 'Enter credential ID',
      required: true,
    },
    {
      id: 'workbookId',
      title: 'Workbook ID',
      type: 'short-input',
      placeholder: 'Enter the workbook ID',
      required: true,
      condition: {
        field: 'operation',
        value: 'read_table',
      },
    },
    {
      id: 'sheetId',
      title: 'Sheet ID',
      type: 'short-input',
      placeholder: 'Enter the sheet ID',
      required: true,
      condition: {
        field: 'operation',
        value: 'read_table',
      },
    },
    {
      id: 'range',
      title: 'Cell range',
      type: 'short-input',
      placeholder: 'A1:Z1000',
      required: true,
      condition: {
        field: 'operation',
        value: 'read_table',
      },
    },
    {
      id: 'operatorId',
      title: 'Operator unionId',
      type: 'short-input',
      placeholder: 'Enter the authorized operator unionId',
      required: true,
      condition: {
        field: 'operation',
        value: ['read_table', 'read_document'],
      },
    },
    {
      id: 'documentId',
      title: 'Document ID',
      type: 'short-input',
      placeholder: 'Enter the document ID',
      required: true,
      condition: {
        field: 'operation',
        value: 'read_document',
      },
    },
    {
      id: 'robotCode',
      title: 'Robot code',
      type: 'short-input',
      placeholder: 'Enter the enterprise app robot code',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
    {
      id: 'openConversationId',
      title: 'Group conversation ID',
      type: 'short-input',
      placeholder: 'cid...',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
    {
      id: 'text',
      title: 'Message text',
      type: 'long-input',
      placeholder: 'Enter the message text',
      required: true,
      condition: {
        field: 'operation',
        value: 'send_message',
      },
    },
  ],
  tools: {
    access: ['dingtalk_read_table', 'dingtalk_read_document', 'dingtalk_send_message'],
    config: { tool: (p) => `dingtalk_${p.operation || 'read_table'}` },
  },
  inputs: {
    workbookId: {
      type: 'string',
      description: 'Workbook ID',
    },
    sheetId: {
      type: 'string',
      description: 'Sheet ID',
    },
    range: {
      type: 'string',
      description: 'Cell range',
    },
    operatorId: {
      type: 'string',
      description: 'Operator unionId',
    },
    documentId: {
      type: 'string',
      description: 'Document ID',
    },
    robotCode: {
      type: 'string',
      description: 'Robot code',
    },
    openConversationId: {
      type: 'string',
      description: 'Group conversation ID',
    },
    text: {
      type: 'string',
      description: 'Message text',
    },
    oauthCredential: {
      type: 'string',
      description: 'Connected app credential ID',
    },
  },
  outputs: {
    rows: {
      type: 'json',
      description: 'Table cell values',
    },
    content: {
      type: 'string',
      description: 'Tab-separated table text',
    },
    blocks: {
      type: 'json',
      description: 'Document blocks',
    },
    processQueryKey: {
      type: 'string',
      description: 'Message tracking key',
    },
  },
}
export const DingTalkBlockMeta = {
  tags: ['messaging', 'knowledge-base'],
  templates: [],
} satisfies BlockMeta
